import { BarChart3, Eye, MapPin, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { categoryById } from "../data/categories";
import type { Place } from "../data/schema";
import { fetchPlaceViewRankings } from "../lib/analytics";
import { regionLabels } from "../lib/labels";

interface ViewRankingsProps {
  places: Place[];
  onSelect: (place: Place) => void;
}

type RankingPeriod = "daily" | "weekly";

export function ViewRankings({ places, onSelect }: ViewRankingsProps) {
  const [activePeriod, setActivePeriod] = useState<RankingPeriod>("daily");
  const [rankings, setRankings] = useState<Record<RankingPeriod, PlaceViewRankingState>>({
    daily: { status: "idle", items: [] },
    weekly: { status: "idle", items: [] },
  });

  const placesById = useMemo(
    () => new Map(places.map((place) => [place.id, place])),
    [places],
  );
  const activeState = rankings[activePeriod];

  useEffect(() => {
    let ignore = false;

    setRankings((current) => ({
      ...current,
      [activePeriod]: { ...current[activePeriod], status: "loading" },
    }));

    fetchPlaceViewRankings(activePeriod)
      .then((items) => {
        if (ignore) return;
        setRankings((current) => ({
          ...current,
          [activePeriod]: { status: "loaded", items },
        }));
      })
      .catch(() => {
        if (ignore) return;
        setRankings((current) => ({
          ...current,
          [activePeriod]: { ...current[activePeriod], status: "error" },
        }));
      });

    return () => {
      ignore = true;
    };
  }, [activePeriod]);

  return (
    <section className="rankings-section" aria-label="많이 본 여행지">
      <div className="section-heading">
        <BarChart3 size={20} aria-hidden="true" />
        <div>
          <h2>많이 본 여행지</h2>
          <p>상세 정보를 많이 살펴본 여행지입니다.</p>
        </div>
        <span>{activeState.items.length}곳</span>
      </div>

      <div className="ranking-tabs" role="tablist" aria-label="조회 기간">
        {[
          { id: "daily", label: "일간" },
          { id: "weekly", label: "주간" },
        ].map(({ id, label }) => (
          <button
            aria-selected={activePeriod === id}
            className={activePeriod === id ? "active" : ""}
            key={id}
            onClick={() => setActivePeriod(id as RankingPeriod)}
            role="tab"
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className="ranking-list" role="tabpanel">
        {activeState.items.map((item) => {
          const place = placesById.get(item.placeId);
          const category = categoryById[item.category];

          return (
            <button
              disabled={!place}
              key={`${activePeriod}-${item.placeId}`}
              onClick={() => place && onSelect(place)}
              type="button"
            >
              <span className="rank-badge">{item.rank}</span>
              <span className="ranking-copy">
                <strong>{item.name}</strong>
                <span>
                  <MapPin size={13} aria-hidden="true" />
                  {regionLabels[item.region]} · {item.district} · {category.name}
                </span>
              </span>
              <span className="ranking-metrics">
                <span>
                  <Eye size={13} aria-hidden="true" />
                  {item.viewCount}
                </span>
                <span>
                  <Users size={13} aria-hidden="true" />
                  {item.uniqueVisitors}
                </span>
              </span>
            </button>
          );
        })}

        {activeState.status === "loading" && <div className="empty">불러오는 중</div>}
        {activeState.status === "error" && <div className="empty">인기 여행지를 불러오지 못했습니다.</div>}
        {activeState.status === "loaded" && activeState.items.length === 0 && (
          <div className="empty">아직 많이 본 여행지가 없습니다.</div>
        )}
      </div>
    </section>
  );
}

interface PlaceViewRankingState {
  status: "idle" | "loading" | "loaded" | "error";
  items: Array<{
    rank: number;
    placeId: string;
    name: string;
    category: Place["category"];
    region: Place["region"];
    district: string;
    viewCount: number;
    uniqueVisitors: number;
  }>;
}
